import React from "react";
import type { EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./SliderDotBtn";
import { PrevButton, NextButton, usePrevNextButtons } from "./SliderArrowBtn";
import useEmblaCarousel from "embla-carousel-react";
import "../styles/slider.css";
import { useEffect, useState } from "react";
import type { classDataProps } from "../types/common";
import { classBelongsToBook } from "../data/examBlock";
import GroupIcon from "@mui/icons-material/Group";

type PropType = {
  slides: number[];
  options?: EmblaOptionsType;
  booksData: { bookUrl: string; bookDesc: string; _id: string }[];
  selectBookId: string;
  setSelectBookId: (bookId: string) => void;
  classData?: classDataProps[];
};

const Slider: React.FC<PropType> = ({
  slides,
  options,
  booksData,
  selectBookId,
  setSelectBookId,
  classData,
}) => {
  const [data, setData] = useState<{ bookUrl: string; bookDesc: string; _id: string }[]>([]);
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  useEffect(() => {
    setData(booksData);
  }, [booksData]);

  // 選中的書不在畫面裡時把輪播捲過去（kanban 1000823）：上傳後導回會由程式選到
  // 歷屆學測／模考，那幾本排在後面，手機一次只看得到兩本，不捲就像沒有任何一本被選中。
  // 使用者自己點的書本來就在畫面裡，slidesInView 擋住不會多跳一下。
  useEffect(() => {
    if (!emblaApi || !selectBookId) return;
    const idx = data.findIndex((book) => book._id === selectBookId);
    if (idx < 0 || emblaApi.slidesInView().includes(idx)) return;
    const snap = emblaApi.internalEngine().slideRegistry.findIndex((group) => group.includes(idx));
    if (snap >= 0) emblaApi.scrollTo(snap);
  }, [emblaApi, data, selectBookId]);

  return (
    <section className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((index) => (
            <div className="embla__slide hover:z-50" key={index}>
              <div
                onClick={() => {
                  if (data[index] && data[index]._id) {
                    console.log('🔄 [書籍切換] 用戶選擇書籍:', data[index].bookDesc, '(ID:', data[index]._id, ')');
                    setSelectBookId(data[index]._id);
                  }
                }}
                className={`slider_card cursor-pointer rounded-lg shadow-md transition-all duration-300 select-none group ${
                  selectBookId === data[index]?._id
                    ? "outline outline-3 outline-secondary outline-offset-4"
                    : "hover:shadow-xl hover:scale-105 hover:-translate-y-1"
                }`}
              >
                {data && data.length > 0 && data[index]?.bookUrl && (
                  <img
                    className="h-full w-full object-cover"
                    src={data[index].bookUrl}
                    alt=""
                  />
                )}
                {Array.isArray(classData) && classData.length > 0 && (
                  <div className="absolute bottom-2 left-0 flex items-center justify-center bg-black/50 rounded-r-sm">
                    {(() => {
                      // 確保 data[index] 存在且有 _id
                      if (!data[index] || !data[index]._id) return null;
                      
                      // 同桌機版:學測/模考書沿用陪練國寫舊班級,書籍歸屬用
                      // classBelongsToBook 而非 bookID 相等(kanban 113525)
                      const filteredClasses = classData.filter(
                        (item) => item && item.bookID && classBelongsToBook(item, data[index]._id)
                      );
                      return filteredClasses.map((item, idx) => (
                        <span
                          key={item._id || idx}
                          className="text-white w-fit h-fit px-1 py-.5 text-nowrap group-hover:inline hidden"
                        >
                          {idx === 0 && <GroupIcon sx={{ mr: 1 }} />}
                          {item.className}
                          {idx !== filteredClasses.length - 1 && "、"}
                        </span>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>

        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={"embla__dot".concat(
                index === selectedIndex ? " embla__dot--selected" : ""
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export { Slider };